'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { type Locale, getLocalePath } from '@/lib/i18n';
import { routeFor } from '@/lib/routes';
import { Button } from '@/components/ui/Button';
import styles from './SolutionComparison.module.css';

interface Solution {
  title: string;
  description: string;
  whenToChoose: string;
  scenarios: string[];
  image: string;
  imageAlt: string;
  link: string;
}

interface SolutionComparisonProps {
  translations: {
    badge: string;
    title: string;
    subtitle: string;
    qrSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    kioskSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    tabletSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    webshopSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    primarySolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    secondarySolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    bottomLine?: string;
    cta?: string;
    allIndustriesLink?: {
      text: string;
      description: string;
    };
  };
  primaryType: 'qr' | 'kiosk' | 'tablet' | 'webshop';
  secondaryType: 'qr' | 'kiosk' | 'tablet' | 'webshop';
  locale: Locale;
}

// Device images mapping
const DEVICE_IMAGES: Record<string, string> = {
  qr: '/images/mockups/qr-mockup.webp',
  kiosk: '/images/mockups/kiosk-mockup.webp',
  tablet: '/images/mockups/tablet-mockup.webp',
  webshop: '/images/webshop-mockups/webshop-anne-max-menu-mobile.webp',
};

export const SolutionComparison: React.FC<SolutionComparisonProps> = ({
  translations,
  primaryType,
  secondaryType,
  locale,
}) => {
  const getDemoUrl = (locale: Locale): string => {
    const demoUrls: Record<Locale, string> = {
      nl: '/demo-aanvragen',
      en: '/request-demo',
      de: '/demo-anfragen',
    };
    return demoUrls[locale];
  };

  const getBranchesUrl = (locale: Locale): string => {
    const branchesUrls: Record<Locale, string> = {
      nl: '/branches',
      en: '/industries',
      de: '/branchen',
    };
    return branchesUrls[locale];
  };

  const primaryLink = getLocalePath(locale, routeFor(primaryType, locale));
  const secondaryLink = getLocalePath(locale, routeFor(secondaryType, locale));

  // Support both naming conventions and all solution types
  const getSolutionByType = (type: string) => {
    switch (type) {
      case 'qr':
        return translations.qrSolution;
      case 'kiosk':
        return translations.kioskSolution;
      case 'tablet':
        return translations.tabletSolution;
      case 'webshop':
        return translations.webshopSolution;
      default:
        return null;
    }
  };

  const primarySolution = translations.primarySolution || getSolutionByType(primaryType);
  const secondarySolution = translations.secondarySolution || getSolutionByType(secondaryType);

  if (!primarySolution || !secondarySolution) {
    console.error('SolutionComparison: Missing solution data', {
      primaryType,
      secondaryType,
      primarySolution,
      secondarySolution,
      translations
    });
    return null;
  }

  const getButtonLabel = (type: string): string => {
    const labels: Record<string, Record<Locale, string>> = {
      qr: {
        nl: 'Meer over QR',
        en: 'More about QR',
        de: 'Mehr über QR',
      },
      kiosk: {
        nl: 'Meer over Kiosk',
        en: 'More about Kiosk',
        de: 'Mehr über Kiosk',
      },
      tablet: {
        nl: 'Meer over Tablet',
        en: 'More about Tablet',
        de: 'Mehr über Tablet',
      },
      webshop: {
        nl: 'Meer over Webshop',
        en: 'More about Webshop',
        de: 'Mehr über Webshop',
      },
    };
    return labels[type]?.[locale] || labels[type]?.['nl'] || 'Meer info';
  };

  return (
    <section className={styles.comparisonSection}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.badge}>{translations.badge}</span>
          <h2 className={styles.title}>{translations.title}</h2>
          <p className={styles.subtitle}>{translations.subtitle}</p>
        </div>

        {/* Comparison Grid */}
        <div className={styles.comparisonGrid}>
          {/* Primary Solution Card */}
          <div className={styles.solutionCard}>
            <div className={styles.cardInner}>
              {/* Device Image */}
              <div className={styles.deviceWrapper}>
                <div className={styles.deviceGlow} />
                <Image
                  src={DEVICE_IMAGES[primaryType]}
                  alt={`${primarySolution.title} Solution`}
                  width={primaryType === 'kiosk' ? 500 : 350}
                  height={primaryType === 'kiosk' ? 800 : 700}
                  className={styles.deviceImage}
                  quality={90}
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIvPjwvc3ZnPg=="
                />
              </div>

              {/* Content */}
              <div className={styles.cardContent}>
                <h3 className={styles.solutionTitle}>{primarySolution.title}</h3>
                <p className={styles.solutionDescription}>{primarySolution.description}</p>

                <div className={styles.whenToChoose}>
                  <span className={styles.whenToChooseLabel}>{primarySolution.whenToChoose}</span>
                  <ul className={styles.scenarios}>
                    {primarySolution.scenarios.map((scenario, index) => (
                      <li key={index} className={styles.scenario}>
                        <span className={styles.checkmark} />
                        {scenario}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link href={primaryLink} className={styles.cardLink}>
                  <Button variant="outline" size="md">
                    {getButtonLabel(primaryType)}
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* VS Badge */}
          <div className={styles.vsBadge}>
            <span>VS</span>
          </div>

          {/* Secondary Solution Card */}
          <div className={styles.solutionCard}>
            <div className={styles.cardInner}>
              {/* Device Image */}
              <div className={styles.deviceWrapper}>
                <div className={styles.deviceGlow} />
                <Image
                  src={DEVICE_IMAGES[secondaryType]}
                  alt={`${secondarySolution.title} Solution`}
                  width={secondaryType === 'kiosk' ? 500 : 350}
                  height={secondaryType === 'kiosk' ? 800 : 700}
                  className={styles.deviceImage}
                  quality={90}
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIvPjwvc3ZnPg=="
                />
              </div>

              {/* Content */}
              <div className={styles.cardContent}>
                <h3 className={styles.solutionTitle}>{secondarySolution.title}</h3>
                <p className={styles.solutionDescription}>{secondarySolution.description}</p>

                <div className={styles.whenToChoose}>
                  <span className={styles.whenToChooseLabel}>{secondarySolution.whenToChoose}</span>
                  <ul className={styles.scenarios}>
                    {secondarySolution.scenarios.map((scenario, index) => (
                      <li key={index} className={styles.scenario}>
                        <span className={styles.checkmark} />
                        {scenario}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link href={secondaryLink} className={styles.cardLink}>
                  <Button variant="outline" size="md">
                    {getButtonLabel(secondaryType)}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={styles.bottomCta}>
          <p className={styles.bottomText}>{translations.bottomLine}</p>
          <div className={styles.ctaGroup}>
            <Link href={getLocalePath(locale, getDemoUrl(locale))}>
              <Button variant="primary" size="lg">
                {translations.cta || primarySolution.cta || 'Demo aanvragen'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

